import { SearchFilterTag } from '@/components';
import { t } from '@/i18n';
import type { ExtractKeyOf } from '@/utils/utils';
import { type FC, useMemo } from 'react';
import { type FieldPathValue, useFormContext, useWatch } from 'react-hook-form';
import type {
  KsAnalysisSearchFormType,
  KsAnalysisSearchKeys,
} from '../../_models/ks-analysis-search';

type FieldKey = (typeof KsAnalysisSearchKeys)[number];

type HiddenFieldKey = ExtractKeyOf<
  FieldKey,
  | 'isSearchTargetProjectName'
  | 'q'
  | 'nestedOrganizationIds'
  | 'corporateNumber'
  | 'aggregationTargetType'
  | 'analysisAxis'
  | 'sheetType'
  | 'fundSheetType'
>;
const hiddenFields: HiddenFieldKey[] = [
  'isSearchTargetProjectName',
  'q',
  'nestedOrganizationIds',
  // corporateNumber は KsPaymentTag が個別表示するためここでは非表示
  'corporateNumber',
  // aggregationTargetType / analysisAxis は KsAggregationTags が個別表示
  'aggregationTargetType',
  'analysisAxis',
  // sheetType / fundSheetType はルート由来で、ユーザーがフォームから操作する値ではないためタグ非表示
  'sheetType',
  'fundSheetType',
];

type Props = { field: FieldKey };
const getValue = <K extends FieldKey>(targetValue: unknown) =>
  targetValue as FieldPathValue<KsAnalysisSearchFormType, K>;

export const KsCommonFilterTag: FC<Props> = ({ field }) => {
  const { setValue, control } = useFormContext<KsAnalysisSearchFormType>();
  const targetValue = useWatch({ control, name: field });
  const value = useMemo(() => {
    if (!targetValue || hiddenFields.includes(field as HiddenFieldKey)) {
      return null;
    }
    switch (field) {
      case 'fiscalYear': {
        const value = getValue<typeof field>(targetValue);
        return `${t('created-fiscal-year')}: ${value?.year()}`;
      }
      case 'projectNumbers': {
        const value = getValue<typeof field>(targetValue);
        return `${t('project-number')}: ${value}`;
      }
      default:
        return field;
    }
  }, [field, targetValue]);
  if (!value) {
    return null;
  }
  return (
    <SearchFilterTag
      label={value}
      // fiscalYear は必須項目のため閉じる動作は無効
      closable={field !== 'fiscalYear'}
      onClose={() => {
        // 単純に null 化する（required な fiscalYear は closable=false のためここに来ない）
        setValue(field, null as never);
      }}
    />
  );
};
